/**
 * useFacetedFilters — shared faceted-filter pipeline for the live menu,
 * brand detail, and strain detail pages.
 *
 * Given a product `pool` and the currently-selected filter arrays, returns:
 *   - applyFilters(arr, excludeAxis?) → array of products matching all
 *     selected filters (excluding the named axis so each facet's option list
 *     can be computed cross-axis aware).
 *   - strainOptions / weightOptions / potencyOptions / brandOptions:
 *     dynamic option lists with counts/labels, ordered for the sidebar.
 *   - filtered: pool with all current filters applied.
 *
 * Multi-select semantics: within an axis OR, across axes AND. Identical to
 * the live menu's behaviour.
 */
import { useCallback, useMemo } from "react";
import { STRAINS, WEIGHTS, POTENCY } from "./MenuFilters";

export default function useFacetedFilters(pool, selections) {
    const {
        selectedStrains = [],
        selectedBrands = [],
        selectedWeights = [],
        selectedPotency = [],
    } = selections || {};

    const products = pool || [];

    const applyFilters = useCallback(
        (input, exclude = null) =>
            (input || []).filter((p) => {
                if (
                    exclude !== "strain" &&
                    !(!selectedStrains.length ||
                        selectedStrains.includes(p.strainType))
                )
                    return false;
                if (
                    exclude !== "brand" &&
                    !(!selectedBrands.length ||
                        selectedBrands.includes(p?.brand?.name || ""))
                )
                    return false;
                if (exclude !== "weight" && selectedWeights.length) {
                    const wantSet = new Set(
                        selectedWeights.map((w) => w.toLowerCase())
                    );
                    if (
                        !(p.variants || []).some((v) =>
                            wantSet.has((v.option || "").toLowerCase())
                        )
                    )
                        return false;
                }
                if (exclude !== "potency" && selectedPotency.length) {
                    const range = p?.potencyThc?.range || [];
                    const upper = Number(range[1] ?? range[0] ?? 0);
                    if (
                        !selectedPotency.some((band) => {
                            const [lo, hi] = band.split("-").map(Number);
                            return upper > lo && upper <= hi;
                        })
                    )
                        return false;
                }
                return true;
            }),
        [selectedStrains, selectedBrands, selectedWeights, selectedPotency]
    );

    const strainOptions = useMemo(() => {
        const f = applyFilters(products, "strain");
        const present = new Set(f.map((p) => p.strainType).filter(Boolean));
        return STRAINS.filter((s) => present.has(s.val));
    }, [products, applyFilters]);

    const weightOptions = useMemo(() => {
        const f = applyFilters(products, "weight");
        const present = new Set();
        for (const p of f) {
            for (const v of p.variants || []) {
                const opt = (v.option || "").trim();
                if (opt) present.add(opt.toLowerCase());
            }
        }
        return WEIGHTS.filter((w) => present.has(w.val.toLowerCase()));
    }, [products, applyFilters]);

    const potencyOptions = useMemo(() => {
        const f = applyFilters(products, "potency");
        return POTENCY.filter((band) => {
            const [lo, hi] = band.val.split("-").map(Number);
            return f.some((p) => {
                const range = p?.potencyThc?.range || [];
                const upper = Number(range[1] ?? range[0] ?? 0);
                return upper > lo && upper <= hi;
            });
        });
    }, [products, applyFilters]);

    const brandOptions = useMemo(() => {
        const f = applyFilters(products, "brand");
        const counts = {};
        for (const p of f) {
            const name = p?.brand?.name;
            if (!name) continue;
            counts[name] = (counts[name] || 0) + 1;
        }
        return Object.entries(counts)
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([name, count]) => ({ name, count }));
    }, [products, applyFilters]);

    const filtered = useMemo(
        () => applyFilters(products),
        [products, applyFilters]
    );

    return {
        applyFilters,
        strainOptions,
        weightOptions,
        potencyOptions,
        brandOptions,
        filtered,
    };
}
